// frontend/src/components/VenueDiscoveryStrip.tsx
import React from 'react';
import { useTheme, t } from '../contexts/ThemeContext';
import { VenueStub } from '../types/api';

interface Props {
	venues: VenueStub[];
	hidden: boolean;
	isMobile: boolean;
}

const CATEGORY_EMOJI: Record<string, string> = {
	bar: '🍸', cocktail: '🍸', nightclub: '🎶', club: '🎶', brewery: '🍺',
	restaurant: '🍽️', cafe: '☕', coffee: '☕', bakery: '🥐',
	museum: '🖼️', gallery: '🎨', park: '🌳', garden: '🌳',
	bookstore: '📚', spa: '💆', bowling: '🎳', cinema: '🎬', hotel: '🏨',
};

function iconFor(v: VenueStub): string {
	const key = (v.category || v.type || '').toLowerCase();
	for (const [needle, emoji] of Object.entries(CATEGORY_EMOJI)) {
		if (key.includes(needle)) return emoji;
	}
	return '📍';
}

const VenueDiscoveryStrip: React.FC<Props> = ({ venues, hidden, isMobile }) => {
	const { isDark } = useTheme();
	const tk = t(isDark);

	if (venues.length === 0) return null;

	return (
		<div style={{
			maxHeight: hidden ? 0 : 900,
			opacity: hidden ? 0 : 1,
			overflow: 'hidden',
			marginBottom: hidden ? 0 : 20,
			transition: 'max-height 0.45s cubic-bezier(0.4,0,0.2,1), opacity 0.35s ease, margin-bottom 0.45s ease',
		}}>
			<div style={{
				background: tk.progressCardBg,
				backdropFilter: 'blur(16px)',
				WebkitBackdropFilter: 'blur(16px)',
				border: `1px solid ${tk.progressCardBorder}`,
				borderRadius: '14px', padding: isMobile ? '14px' : '18px',
			}}>
				<div style={{ fontSize: '0.78rem', fontWeight: 600, color: tk.textMuted, marginBottom: 10 }}>
					Discovering venues…
				</div>
				<div style={{
					display: 'flex', gap: 10,
					flexWrap: isMobile ? 'nowrap' : 'wrap',
					overflowX: isMobile ? 'auto' : 'visible',
					scrollSnapType: isMobile ? 'x proximity' : undefined,
					paddingBottom: isMobile ? 4 : 0,
				}}>
					{venues.map((v, i) => (
						<div key={`${v.name}|${v.address_hint || ''}`} style={{
							flex: isMobile ? '0 0 220px' : '1 1 220px',
							maxWidth: isMobile ? 220 : 260,
							scrollSnapAlign: isMobile ? 'start' : undefined,
							background: v.status === 'researched' ? tk.stepCardBg : tk.stepCardIdleBg,
							border: `1px solid ${v.status === 'researched' ? tk.stepCardBorder : tk.stepCardIdleBorder}`,
							borderRadius: 10, padding: 12,
							animation: 'slideInStep 0.4s ease both',
							animationDelay: `${Math.min(i, 10) * 80}ms`,
							transition: 'background 0.3s, border-color 0.3s',
						}}>
							<div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
								<span style={{ fontSize: '1.3rem', lineHeight: 1 }}>{iconFor(v)}</span>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{
										fontWeight: 600, fontSize: '0.85rem', color: tk.textPrimary,
										whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
									}}>
										{v.name}
									</div>
									<div style={{ fontSize: '0.72rem', color: tk.textMuted }}>
										{[v.neighborhood, v.google_rating ? `⭐ ${v.google_rating}` : null]
											.filter(Boolean).join(' • ')}
									</div>
								</div>
							</div>

							{v.status === 'found' && (
								<div style={{
									marginTop: 8, height: 8, borderRadius: 4,
									background: isDark
										? 'linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.18), rgba(255,255,255,0.06))'
										: 'linear-gradient(90deg, #e2e8f0, #f1f5f9, #e2e8f0)',
									backgroundSize: '200% 100%',
									animation: 'shimmer-bg 1.6s linear infinite',
								}} />
							)}

							{v.status === 'researched' && v.research && (
								<div style={{
									marginTop: 8, fontSize: '0.74rem', color: tk.textSecondary, lineHeight: 1.4,
									animation: 'fadeInUp 0.3s ease',
								}}>
									{v.research.description_clean && (
										<div style={{ marginBottom: 4 }}>
											{v.research.description_clean.slice(0, 90)}
											{v.research.description_clean.length > 90 ? '…' : ''}
										</div>
									)}
									<div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
										{v.research.price_tier && (
											<span style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: 6, background: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.1)', color: isDark ? '#6ee7b7' : '#15803d' }}>
												{v.research.price_tier}
											</span>
										)}
										{v.research.crowd_level && (
											<span style={{ fontSize: '0.68rem', padding: '2px 7px', borderRadius: 6, background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)', color: isDark ? '#93c5fd' : '#2563eb' }}>
												{v.research.crowd_level}
											</span>
										)}
									</div>
								</div>
							)}

							{v.status === 'failed' && (
								<div style={{ marginTop: 8, fontSize: '0.72rem', color: tk.textMuted, fontStyle: 'italic' }}>
									Details unavailable
								</div>
							)}

							{v.status === 'skipped' && (
								<div style={{ marginTop: 8, fontSize: '0.72rem', color: tk.textMuted, fontStyle: 'italic' }}>
									Not researched this round
								</div>
							)}
						</div>
					))}
				</div>
			</div>

			<style>{`
        @keyframes slideInStep {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
		</div>
	);
};

export default VenueDiscoveryStrip;
